/**
 * Mitarbeiterverzeichnis (/mitarbeiter): durchsuchbares Grid mit Live-Status
 * (G-3), Stellen-Badges und Beauftragungs-Ampel (G-9).
 */

import { useEffect, useMemo, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Search, UserRound } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
  EntityFormSheet,
  type FieldDef,
  type FormValues,
} from "@/components/admin/EntityFormSheet"
import { AssignmentBadge, EmployeeStatusBadge } from "@/components/employees/badges"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useCan } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type {
  Anrede,
  EmployeeCreate,
  EmployeeListItem,
  EmploymentStatusFilter,
  Geschlecht,
  Mitarbeitertyp,
} from "@/types"

const PAGE_SIZE = 50

const STATUS_CHIPS: { value: EmploymentStatusFilter | ""; label: string }[] = [
  { value: "", label: "Alle" },
  { value: "active", label: "Aktiv" },
  { value: "sick", label: "Krank" },
  { value: "vacation", label: "Urlaub" },
  { value: "left", label: "Ausgeschieden" },
]

const GENDER_OPTIONS: { value: Geschlecht; label: string }[] = [
  { value: "MAENNLICH", label: "Männlich" },
  { value: "WEIBLICH", label: "Weiblich" },
  { value: "DIVERS", label: "Divers" },
]

const SALUTATION_OPTIONS: { value: Anrede; label: string }[] = [
  { value: "HERR", label: "Herr" },
  { value: "FRAU", label: "Frau" },
]

const TYPE_OPTIONS: { value: Mitarbeitertyp; label: string }[] = [
  { value: "INTERNER_MITARBEITER", label: "Interner Mitarbeiter" },
  { value: "EXTERNER_BERATER", label: "Externer Berater" },
  { value: "ZEITARBEITNEHMER", label: "Zeitarbeitnehmer" },
  { value: "RENTNER", label: "Rentner" },
]

const CREATE_FIELDS: FieldDef[] = [
  {
    key: "salutation",
    label: "Anrede",
    kind: "select",
    emptyOption: "— keine Anrede —",
    options: SALUTATION_OPTIONS,
  },
  { key: "firstName", label: "Vorname", kind: "text", required: true },
  { key: "lastName", label: "Nachname", kind: "text", required: true },
  { key: "gender", label: "Geschlecht", kind: "select", required: true, options: GENDER_OPTIONS },
  { key: "birthDate", label: "Geburtsdatum", kind: "date" },
  {
    key: "employeeType",
    label: "Mitarbeitertyp",
    kind: "select",
    emptyOption: "— kein Typ —",
    options: TYPE_OPTIONS,
  },
  { key: "profession", label: "Beruf", kind: "text" },
  { key: "hireDate", label: "Eintritt", kind: "date" },
]

/** 300ms-Debounce fuer das Suchfeld */
function useDebounced(value: string, delay = 300): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function EmployeesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canWrite = useCan("personal_schreiben")

  const [search, setSearch] = useState("")
  const q = useDebounced(search)
  const [status, setStatus] = useState<EmploymentStatusFilter | "">("")
  const [hasAccountOnly, setHasAccountOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)

  // Filterwechsel setzt die Seite zurueck
  useEffect(() => setPage(1), [q, status, hasAccountOnly])

  const params = {
    page,
    size: PAGE_SIZE,
    q: q || undefined,
    status: status || undefined,
    hasAccount: hasAccountOnly ? true : undefined,
  }
  const query = useQuery({
    queryKey: ["employees", params],
    queryFn: () => api.employees.list(params),
    placeholderData: keepPreviousData,
  })

  const create = useMutation({
    mutationFn: (body: EmployeeCreate) => api.employees.create(body),
    onSuccess: (detail) => {
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      toast.success(`Mitarbeiter ${detail.displayName} angelegt`)
      setCreateOpen(false)
      navigate(`/mitarbeiter/${detail.id}`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function submitCreate(values: FormValues) {
    const s = (key: string) => {
      const v = values[key]
      return typeof v === "string" && v.trim() !== "" ? v.trim() : null
    }
    create.mutate({
      firstName: s("firstName") ?? "",
      lastName: s("lastName") ?? "",
      gender: (s("gender") ?? "MAENNLICH") as Geschlecht,
      salutation: (s("salutation") as Anrede | null) ?? null,
      birthDate: s("birthDate"),
      employeeType: (s("employeeType") as Mitarbeitertyp | null) ?? null,
      profession: s("profession"),
      hireDate: s("hireDate"),
    })
  }

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<EmployeeListItem>[]>(
    () => [
      {
        id: "status",
        header: "Status",
        accessorFn: (emp) => emp.assignmentStatus ?? "",
        cell: ({ row }) => <EmployeeStatusBadge employee={row.original} />,
        meta: { headClassName: "w-32" },
      },
      {
        id: "name",
        header: "Name",
        accessorFn: (emp) => emp.displayName,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <UserRound className="size-4 text-muted-foreground" />
            <span className="font-medium">{row.original.displayName}</span>
            {row.original.username && (
              <span className="text-xs text-muted-foreground">{row.original.username}</span>
            )}
          </div>
        ),
      },
      {
        id: "positions",
        header: "Stellen",
        accessorFn: (emp) => emp.positionNames.join(", "),
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.positionNames.length === 0 && (
              <span className="text-xs text-muted-foreground">—</span>
            )}
            {row.original.positionNames.map((name) => (
              <Badge key={name} variant="secondary">
                {name}
              </Badge>
            ))}
          </div>
        ),
      },
      {
        id: "assignments",
        header: "Beauftragungen",
        accessorFn: (emp) => emp.assignmentStatus,
        cell: ({ row }) => <AssignmentBadge status={row.original.assignmentStatus} />,
        meta: { headClassName: "w-48" },
      },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Mitarbeiter</h1>
        {canWrite && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus /> Neuer Mitarbeiter
          </Button>
        )}
      </div>

      {/* Filterleiste */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Suchen (Name, Benutzername) …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => setStatus(chip.value)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                status === chip.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-transparent text-muted-foreground hover:bg-accent"
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Switch id="has-account" checked={hasAccountOnly} onCheckedChange={setHasAccountOnly} />
          <Label htmlFor="has-account" className="text-sm font-normal text-muted-foreground">
            Nur mit Account
          </Label>
        </div>
      </div>

      {/* Verzeichnis-Grid */}
      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="employees"
              // Server-Suche oben filtert bereits ueber ALLE Mitarbeiter —
              // der Grid-Schnellfilter (nur geladene Seite) wuerde verwirren
              enableQuickFilter={false}
              emptyMessage="Keine Mitarbeiter gefunden."
              onRowClick={(emp) => navigate(`/mitarbeiter/${emp.id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {total} Mitarbeiter
          {pageCount > 1 && ` — Seite ${page} von ${pageCount}`}
        </span>
        {pageCount > 1 && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Zurück
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Weiter
            </Button>
          </div>
        )}
      </div>

      <EntityFormSheet
        sheetKey="entity-form-employees"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Neuer Mitarbeiter"
        description="Pflichtfelder: Nachname, Vorname, Geschlecht — der Benutzername wird automatisch generiert (G-1)."
        fields={CREATE_FIELDS}
        initialValues={{
          salutation: "",
          firstName: "",
          lastName: "",
          gender: "",
          birthDate: "",
          employeeType: "",
          profession: "",
          hireDate: "",
        }}
        pending={create.isPending}
        onSubmit={submitCreate}
      />
    </div>
  )
}
